import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { AdminStartupListItem } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { Linkedin } from 'lucide-react'
import { useState } from 'react'
import { ExperimentArmBadges } from '../../../features/admin/components/experiment-arms'
import {
  useAdminStartupsQuery,
  useAdminTriggerPrescreenMutation,
} from '../../../features/admin/hooks/use-admin-api'
import { useAdminBackfillTeamMemberLinkedInMutation } from '../../../hooks/api'

type SearchParams = {
  page?: number
  search?: string
  hasPrescreen?: boolean
}

type HomeStateKey = NonNullable<AdminStartupListItem['homeState']>['key']

/**
 * Ops-facing shorthand for each Home state. Deliberately not the founder-facing
 * card copy: this column is scanned down a table to find who is stuck, so it
 * wants terse and comparable, not persuasive. Typed against the generated key
 * union, so a new state cannot be added without labelling it here.
 */
const HOME_STATE_LABELS: Record<HomeStateKey, string> = {
  QUEST_UPLOAD_DECK: 'Upload deck',
  QUEST_READING_DECK: 'Reading deck',
  QUEST_READ_FAILED: 'Read failed',
  QUEST_COMPLETE_PROFILE: 'Complete profile',
  QUEST_SHARE: 'Share step',
  QUEST_FINDING_INVESTORS: 'Finding investors',
  QUEST_REVIEW_MESSAGES: 'Review messages',
  QUEST_APPROVE_OUTREACH: 'Approve contacts',
  QUEST_CONNECT_LINKEDIN: 'Connect LinkedIn',
  QUEST_PREPARING_LINK: 'Preparing link',
  QUEST_LAUNCH_CAMPAIGN: 'Ready to launch',
  QUEST_LIVE: 'Live',
  QUEST_PAUSED: 'Paused by founder',
  // Distinguishes the founder's own disconnect from an unexpected one — the
  // two look identical in the data apart from disconnect_requested_at.
  QUEST_RECONNECT: 'Reconnect (chosen)',
  ALERT_RECONNECT: 'Disconnected',
  ALERT_ADD_FUNDS: 'Needs funds',
  ALERT_SYSTEM_STALL: 'Stalled (ours)',
  ALERT_QUEUE_DRAINED: 'Queue drained',
  MOMENT_REPLY: 'Reply waiting',
  MOMENT_OUTCOME_VERIFYING: 'Outcome verifying',
  MOMENT_OUTCOME_CONFIRMED: 'Call secured',
}

export const Route = createFileRoute('/admin/startups/')({
  component: AdminStartupsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
    hasPrescreen: search.hasPrescreen === false ? false : undefined,
  }),
})

const LIMIT = 20

function formatDate(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function AdminStartupsPage() {
  const { page = 1, search, hasPrescreen } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')
  const backfill = useAdminBackfillTeamMemberLinkedInMutation()
  const triggerPrescreen = useAdminTriggerPrescreenMutation()

  const startupsQuery = useAdminStartupsQuery({
    page,
    limit: LIMIT,
    search: search || undefined,
    hasPrescreen,
  })

  const startups = startupsQuery.data?.data?.items ?? []
  const total = startupsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const handleBackfill = () => {
    backfill.mutate(
      {},
      {
        onSuccess: (response) => {
          const scheduled = response.data?.scheduled ?? 0
          alert(`Scheduled LinkedIn scrape for ${scheduled} team member(s).`)
        },
        onError: (error) => {
          alert(`Backfill failed: ${error.message ?? 'Unknown error'}`)
        },
      }
    )
  }

  const handleTriggerPrescreen = (startup: AdminStartupListItem) => {
    if (!startup.pitchDeckDocumentId) return
    triggerPrescreen.mutate(
      { body: { startupId: startup.id, documentId: startup.pitchDeckDocumentId } },
      {
        onSuccess: () => alert(`Prescreen triggered for ${startup.name}.`),
        onError: (error) => alert(`Trigger failed: ${error.message ?? 'Unknown error'}`),
      }
    )
  }

  const columns: TableColumn<AdminStartupListItem>[] = [
    {
      header: 'ID',
      width: '80px',
      render: (startup) => (
        <Link
          to="/admin/startups/$id"
          params={{ id: startup.id }}
          className="font-mono text-xs text-primary hover:underline"
        >
          {startup.id.slice(0, 8)}
        </Link>
      ),
    },
    {
      header: 'Name',
      render: (startup) => <CellPrimary>{startup.name}</CellPrimary>,
    },
    {
      header: 'Founder',
      render: (startup) =>
        startup.founder ? (
          <Link
            to="/admin/users/$id"
            params={{ id: startup.founder.id }}
            className="text-sm text-primary hover:underline"
          >
            {startup.founder.name || startup.founder.email}
          </Link>
        ) : (
          <CellText>{'—'}</CellText>
        ),
    },
    {
      header: 'Home state',
      width: '160px',
      render: (startup) => {
        const state = startup.homeState
        if (!state) return <CellText>{'—'}</CellText>
        // Red covers the alert states plus the two SLA flags, which mark a
        // founder waiting on something of ours that silently never finished.
        const needsAttention = state.isAlert || state.readSlaExceeded || state.matchingSlaExceeded
        const flags = [
          state.readSlaExceeded ? 'read SLA' : null,
          state.matchingSlaExceeded ? 'matching SLA' : null,
        ].filter(Boolean)
        return (
          <div className="flex flex-col gap-0.5">
            <Badge variant={needsAttention ? 'destructive' : 'outline'}>
              {HOME_STATE_LABELS[state.key] ?? state.key}
            </Badge>
            {flags.length > 0 && (
              <span className="text-[10px] text-muted-foreground">{flags.join(' · ')}</span>
            )}
          </div>
        )
      },
    },
    {
      // Allocation balance is only checkable in aggregate, which is why this
      // column exists at all — the detail page shows one founder's arms, this
      // shows whether the split is even. "Not enrolled" is deliberately distinct
      // from "both control": a founder with no arms is not in the experiment,
      // and counting them as control would bias the split.
      header: 'Experiment',
      width: '150px',
      render: (startup) => <ExperimentArmBadges arms={startup.experimentArms} />,
    },
    {
      header: 'Balances',
      width: '140px',
      render: (startup) => (
        <CellText>
          <span className="tabular-nums">
            {startup.sessionsAvailable}S / {startup.evaluationsAvailable}E
          </span>
        </CellText>
      ),
    },
    {
      header: 'Prescreen',
      width: '110px',
      render: (startup) =>
        startup.hasPrescreen ? (
          <Badge variant="success" size="sm">
            Yes
          </Badge>
        ) : (
          <Badge variant="secondary" size="sm">
            No
          </Badge>
        ),
    },
    {
      header: 'Pitch deck',
      width: '110px',
      render: (startup) =>
        startup.pitchDeckDocumentId ? (
          <Badge variant="outline" size="sm">
            Linked
          </Badge>
        ) : (
          <CellText>{'—'}</CellText>
        ),
    },
    {
      header: '',
      width: '160px',
      render: (startup) => {
        if (startup.hasPrescreen) return null
        const isTriggering =
          triggerPrescreen.isPending && triggerPrescreen.variables?.body?.startupId === startup.id
        return (
          <Button
            variant="outline"
            size="sm"
            disabled={!startup.pitchDeckDocumentId || triggerPrescreen.isPending}
            title={startup.pitchDeckDocumentId ? undefined : 'No pitch deck linked'}
            onClick={() => handleTriggerPrescreen(startup)}
          >
            {isTriggering ? 'Triggering…' : 'Trigger prescreen'}
          </Button>
        )
      },
    },
    {
      header: 'Created',
      width: '200px',
      render: (startup) => <CellText>{formatDate(startup.createdAt)}</CellText>,
    },
  ]

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-2">
        <SearchInput
          placeholder="Search startups..."
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() =>
            navigate({ search: { page: 1, search: searchInput || undefined, hasPrescreen } })
          }
        />
        <div className="flex items-center gap-1">
          <Button
            variant={hasPrescreen === undefined ? 'default' : 'outline'}
            size="sm"
            onClick={() => navigate({ search: { page: 1, search, hasPrescreen: undefined } })}
          >
            All
          </Button>
          <Button
            variant={hasPrescreen === false ? 'default' : 'outline'}
            size="sm"
            onClick={() => navigate({ search: { page: 1, search, hasPrescreen: false } })}
          >
            Without prescreen
          </Button>
        </div>
        <SecondaryButton
          size="sm"
          icon={<Linkedin />}
          onClick={handleBackfill}
          loading={backfill.isPending}
        >
          {backfill.isPending ? 'Scheduling…' : 'Backfill LinkedIn'}
        </SecondaryButton>
      </div>
      <DataTable
        columns={columns}
        data={startups}
        isLoading={startupsQuery.isLoading}
        emptyMessage="No startups found."
        keyExtractor={(s) => s.id}
        // Ten columns, six of them fixed-width. Below this the Name and
        // Founder cells get squeezed until their text overlaps, so scroll
        // instead. Raised by the Experiment column's 150px.
        minWidth="1330px"
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search, hasPrescreen } })}
      />
    </div>
  )
}
